import React, { useCallback, useState } from 'react'
import { Stack, Typography, useTheme } from '@mui/material'
import Touchable from '@components/Touchable'
import { HiCheckCircle } from 'react-icons/hi'
import { useTranslation } from 'react-i18next'
import BottomSheet from '@components/BottomSheet'
import { getChainIcon } from '@plug-core/utils/getChainIcon'
import { Chain, ChainOption } from '@plug-core/interfaces/common'

export const SUPPORTED_CHAINS = [
  Chain.ICP,
  Chain.BTC,
  Chain.ETH,
  Chain.SOL,
  Chain.ODIN,
  Chain.ARB,
  Chain.BASE,
]

interface ChainPickerProps {
  chains: ChainOption[]
  initialChain: ChainOption
  chainSelectVisible: boolean
  setChainSelectVisible: (b: boolean) => void
  onChainChange: (newChain: ChainOption) => void
}

const ChainPicker: React.FC<ChainPickerProps> = ({
  chains,
  initialChain,
  chainSelectVisible,
  setChainSelectVisible,
  onChainChange,
}) => {
  const { t } = useTranslation()
  const { palette } = useTheme()
  const [chainMode, setChainMode] = useState(initialChain)

  const onSelectChain = useCallback(
    (mode: ChainOption) => {
      setChainMode(mode)
      onChainChange(mode)
      setChainSelectVisible(false)
    },
    [onChainChange, setChainMode, setChainSelectVisible]
  )

  return (
    <BottomSheet visible={chainSelectVisible} onBackdropClick={() => setChainSelectVisible(false)}>
      <Stack pt={2} pb={4} spacing={2}>
        <Typography variant="h6" textAlign="center">
          {t(`chains.select`)}
        </Typography>
        <Stack spacing={1.5}>
          {chains.map((mode) => (
            <Touchable key={mode} onClick={() => onSelectChain(mode)}>
              <Stack
                direction="row"
                alignItems="center"
                pl={2}
                pr={2}
                spacing={1}
                justifyContent="space-between"
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <img src={getChainIcon(mode)} width={30} height={30} />
                  <Typography fontWeight="bold">{t(`chains.long.${mode}`)}</Typography>
                </Stack>
                {chainMode === mode && <HiCheckCircle size={20} color={palette.rainbow.green} />}
              </Stack>
            </Touchable>
          ))}
        </Stack>
      </Stack>
    </BottomSheet>
  )
}

export default ChainPicker
